import { Link } from "@/lib/router-compat";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  Shield, Clock, Trophy, Brain, Calculator, Image, Edit3, FileText,
  Layers, Palette, Users, ChevronRight, Lock,
  FileCheck,
} from "lucide-react";
import sentinelforgeLogo from "@/assets/sentinelforge-logo.webp";
import { SUBSCRIPTION_TIERS } from "@/lib/subscription-tiers";
import { motion } from "framer-motion";
import HeroSection from "@/components/landing/HeroSection";
import ROICalculator from "@/components/landing/ROICalculator";
import IndustryGrid from "@/components/landing/IndustryGrid";
import BetaTesterCTA from "@/components/landing/BetaTesterCTA";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: "easeOut" as const },
  }),
};

const Industries = () => {
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* NAV */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-border/50 bg-background/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={sentinelforgeLogo} alt="Sentinel Forge" className="h-7 w-7 object-contain" />
            <span className="text-lg font-bold text-foreground tracking-tight">Sentinel Forge</span>
          </Link>
          <div className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
            <Link to="/data-safety" className="hover:text-foreground transition-colors">Your Data Is Safe</Link>
            <Link to="/pricing" className="hover:text-foreground transition-colors">Pricing</Link>
            <Link to="/demo" className="hover:text-foreground transition-colors">Demo</Link>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/auth">
              <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">Sign In</Button>
            </Link>
            <Link to="/auth?signup">
              <Button size="sm" className="gradient-primary text-primary-foreground shadow-glow hover:opacity-90">Start Free Trial</Button>
            </Link>
          </div>
        </div>
      </nav>

      <main>
        {/* HERO */}
        <HeroSection />

        {/* VALUE PROPOSITIONS */}
        <section className="py-20 md:py-28 border-t border-border/50">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  icon: Clock,
                  title: "Save Hours of Work",
                  description: "Automated proposal creation and staffing calculations eliminate manual busywork so you can focus on winning business.",
                },
                {
                  icon: Trophy,
                  title: "Win More Contracts",
                  description: "Clean, standardized, professional proposals that impress clients and set you apart from the competition.",
                },
                {
                  icon: Shield,
                  title: "Built for Service Companies",
                  description: "Designed by industry professionals with real operational experience across security, cleaning, PI, and more.",
                },
              ].map((item, i) => (
                <motion.div key={item.title} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }} variants={fadeUp}>
                  <Card className="border-border bg-card shadow-card h-full hover:border-primary/30 transition-colors duration-300">
                    <CardContent className="p-8">
                      <div className="h-12 w-12 rounded-xl gradient-accent flex items-center justify-center mb-5">
                        <item.icon className="h-6 w-6 text-primary" />
                      </div>
                      <h2 className="text-xl font-semibold text-foreground mb-3">{item.title}</h2>
                      <p className="text-muted-foreground leading-relaxed">{item.description}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ROI CALCULATOR */}
        <ROICalculator />

        {/* FEATURES */}
        <section id="features" className="py-20 md:py-28 border-t border-border/50 bg-secondary/20">
          <div className="max-w-6xl mx-auto px-6">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0} className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Everything You Need to{" "}
                <span className="text-gradient">Close Deals</span>
              </h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                From AI-powered analysis to branded PDF exports, SentinelForge handles the entire proposal workflow.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                { icon: Brain, label: "AI Threat Assessments" },
                { icon: Calculator, label: "Staffing Calculators" },
                { icon: Image, label: "Image Uploads & Captions" },
                { icon: Edit3, label: "Editable Sections" },
                { icon: FileText, label: "PDF Export" },
                { icon: Layers, label: "Subscription Tiers" },
                { icon: Palette, label: "White-Labeling" },
                { icon: Users, label: "Multi-User Access" },
              ].map((feat, i) => (
                <motion.div key={feat.label} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-30px" }} variants={fadeUp}>
                  <div className="flex items-center gap-4 p-5 rounded-xl border border-border bg-card hover:border-primary/30 transition-colors duration-300">
                    <div className="h-10 w-10 rounded-lg gradient-accent flex items-center justify-center shrink-0">
                      <feat.icon className="h-5 w-5 text-primary" />
                    </div>
                    <span className="text-sm font-medium text-foreground">{feat.label}</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* INDUSTRY GRID */}
        <IndustryGrid />

        {/* PRICING PREVIEW */}
        <section id="pricing" className="py-20 md:py-28 border-t border-border/50">
          <div className="max-w-6xl mx-auto px-6">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0} className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Simple, Transparent{" "}
                <span className="text-gradient">Pricing</span>
              </h2>
              <p className="text-muted-foreground text-lg max-w-xl mx-auto">
                Plans that scale with your business. Start small, grow fast.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {(Object.entries(SUBSCRIPTION_TIERS) as [string, typeof SUBSCRIPTION_TIERS[keyof typeof SUBSCRIPTION_TIERS]][])
                .filter(([_, tier]) => !('internal' in tier && tier.internal))
                .map(([key, tier], i) => {
                  const isPro = key === "professional";
                  return (
                    <motion.div key={key} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-30px" }} variants={fadeUp}>
                      <Card className={`border-border bg-card shadow-card h-full relative ${isPro ? "border-primary/50 shadow-glow" : ""}`}>
                        {isPro && (
                          <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full gradient-primary text-primary-foreground text-xs font-semibold">
                            Most Popular
                          </div>
                        )}
                        <CardContent className="p-6 flex flex-col h-full">
                          <h3 className="text-lg font-semibold text-foreground mb-1">{tier.name}</h3>
                          <div className="mb-5">
                            {tier.price !== null ? (
                              <div className="flex items-baseline gap-1">
                                <span className="text-3xl font-bold text-foreground">${tier.price}</span>
                                <span className="text-muted-foreground text-sm">/mo</span>
                              </div>
                            ) : (
                              <span className="text-2xl font-bold text-foreground">Custom</span>
                            )}
                          </div>
                          <ul className="space-y-2.5 flex-1 mb-6">
                            {tier.features.slice(0, 4).map((f) => (
                              <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
                                <FileCheck className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                                {f}
                              </li>
                            ))}
                          </ul>
                          <Link to="/auth?signup" className="w-full">
                            <Button
                              variant={isPro ? "default" : "outline"}
                              className={`w-full ${isPro ? "gradient-primary text-primary-foreground shadow-glow hover:opacity-90" : "border-border text-foreground hover:bg-secondary"}`}
                            >
                              Start Free Trial
                            </Button>
                          </Link>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
            </div>

            {/* Enterprise CTA */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={4} className="mt-8">
              <Card className="border-[hsl(38,92%,50%)]/40 bg-gradient-to-br from-card to-[hsl(38,92%,50%)]/5 shadow-card overflow-hidden relative">
                <div className="absolute top-0 right-0 w-64 h-64 bg-[hsl(38,92%,50%)]/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
                <CardContent className="p-8 relative">
                  <div className="flex flex-col md:flex-row md:items-start gap-8">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="h-10 w-10 rounded-xl bg-[hsl(38,92%,50%)]/10 flex items-center justify-center">
                          <Lock className="h-5 w-5 text-[hsl(38,92%,50%)]" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-foreground">Enterprise</h3>
                          <p className="text-sm text-[hsl(38,92%,50%)]">Custom Pricing</p>
                        </div>
                      </div>
                      <p className="text-muted-foreground mb-4">
                        For organizations that need advanced customization, API access, and SLA‑backed support.
                      </p>
                      <p className="text-sm text-muted-foreground mb-3">Includes everything in Agency, plus:</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6">
                        {SUBSCRIPTION_TIERS.enterprise.features.map((f) => (
                          <div key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
                            <FileCheck className="h-4 w-4 text-[hsl(38,92%,50%)] mt-0.5 shrink-0" />
                            {f}
                          </div>
                        ))}
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">Pricing is based on:</p>
                      <div className="flex flex-wrap gap-2 mb-6">
                        {SUBSCRIPTION_TIERS.enterprise.pricingFactors.map((f) => (
                          <span key={f} className="px-3 py-1 rounded-full text-xs font-medium border border-[hsl(38,92%,50%)]/30 text-[hsl(38,92%,50%)] bg-[hsl(38,92%,50%)]/5">{f}</span>
                        ))}
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      <a href="mailto:sales@sentinelforgesystems.com?subject=Enterprise%20Plan%20Inquiry">
                        <Button className="bg-[hsl(38,92%,50%)] hover:bg-[hsl(38,92%,40%)] text-foreground font-semibold px-8">
                          Contact Sales
                        </Button>
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <div className="text-center mt-10">
              <Link to="/pricing">
                <Button variant="link" className="text-primary hover:text-primary/80">
                  View full pricing details <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="py-20 md:py-28 border-t border-border/50 bg-secondary/20">
          <div className="max-w-3xl mx-auto px-6">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0} className="text-center mb-14">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Frequently Asked{" "}
                <span className="text-gradient">Questions</span>
              </h2>
            </motion.div>

            <Accordion type="single" collapsible className="space-y-3">
              {[
                { q: "How does the free trial work?", a: "Sign up and get instant access to the Starter plan for 7 days. No credit card required. You can generate up to 10 proposals during the trial to see how SentinelForge works for your business." },
                { q: "Can I use this for industries other than security?", a: "Security is currently live and fully available. Commercial cleaning, event staffing, and private investigation are actively in development and coming soon. We're continually expanding to more service industries — join the waitlist on any industry page to be notified when it launches." },
                { q: "Is my proposal data private?", a: "Absolutely. Your proposals, pricing, templates, and client data are encrypted and isolated to your account. We cannot see or access your content. Read our full data safety commitment." },
                { q: "Can I export proposals as PDFs?", a: "Yes. All plans include PDF export with your company branding, logo, and custom colors." },
                { q: "Do you offer team accounts?", a: "Yes, the Agency plan ($99/month) includes team member accounts, bulk proposal generation, and a client portal for sharing proposals." },
              ].map((faq, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border border-border rounded-xl px-6 bg-card">
                  <AccordionTrigger className="text-foreground font-medium text-left hover:no-underline">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* BETA TESTER CTA */}
        <BetaTesterCTA />
      </main>

      {/* FOOTER */}
      <footer className="border-t border-border/50 py-10">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-6 text-xs text-muted-foreground">
            <Link to="/terms" className="hover:text-foreground transition-colors">Terms</Link>
            <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
            <Link to="/data-safety" className="hover:text-foreground transition-colors">Data Safety</Link>
          </div>
          <p className="text-xs text-muted-foreground mt-4">© {new Date().getFullYear()} SentinelForge Systems. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Industries;
